import * as yup from 'yup';
import { DeskingOffer } from '@upstart/harmony/lib/legos/deskingTypes';
import { FinanceType } from '@upstart/harmony/lib/legos/constants';

export type DeskingOfferError = {
  deskingOfferId: string;
  financeType?: FinanceType;
  isSelected: boolean;
  errors?: Record<string, string>;
};

const rebate = yup.number().label('Rebate');
const tax = yup
  .number()
  .nullable()
  .label('Tax');

const sharedSchema = {
  tax,
  rebate,
  price: yup.number().label('Vehicle Price'),
  discount: yup.number().label('Discount'),
  tradeInAllowance: yup.number().label('Trade In Allowance'),
  tradePayoff: yup.number().label('Trade In Payoff'),
  invoice: yup.number().label('Invoice'),
  freight: yup.number().label('Freight'),
  fees: yup
    .object()
    .nullable()
    .label('Dealer Fees'),
};

const cashOfferSchema = yup.object().shape({
  ...sharedSchema,
});

const sharedFinancingSchema = {
  ...sharedSchema,
  months: yup
    .number()
    .integer()
    .moreThan(0)
    .required()
    .label('Months'),
  daysToFirstPayment: yup
    .number()
    .min(0)
    .max(365)
    .required()
    .label('Days to First Payment')
    // eslint-disable-next-line no-template-curly-in-string
    .typeError('${label} must be between 0 and 365'),
};

const purchaseOfferSchema = yup.object().shape({
  ...sharedFinancingSchema,
  apr: yup
    .number()
    .min(0)
    .required()
    .label('APR'),
});

const leaseOfferSchema = yup.object().shape({
  ...sharedFinancingSchema,
  mileage: yup
    .number()
    .integer()
    .moreThan(0)
    .required()
    .label('mileage'),
  rate: yup
    .number()
    .min(0)
    .required()
    .label('rate'),
  residual: yup
    .number()
    .moreThan(0)
    .required()
    .label('residual'),
});

export function validateDeskingOffer(offer: DeskingOffer): DeskingOfferError {
  let errors: DeskingOfferError['errors'];

  try {
    switch (offer.data?.financeType) {
      case FinanceType.PURCHASE: {
        purchaseOfferSchema.validateSync(offer.data, {
          abortEarly: false,
        });
        break;
      }
      case FinanceType.LEASE:
        leaseOfferSchema.validateSync(offer.data, { abortEarly: false });
        break;
      case FinanceType.CASH:
        cashOfferSchema.validateSync(offer.data, { abortEarly: false });
        break;
    }
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      errors = {};
      for (const fieldError of error.inner) {
        if (fieldError.path) {
          errors[fieldError.path] = fieldError.message;
        }
      }
    }
  }

  return {
    deskingOfferId: offer.id,
    financeType: offer.data?.financeType,
    isSelected: !!offer.isSelectedOffer,
    errors,
  };
}
